/**
 * Revenue Dashboard Page
 *
 * Displays comprehensive earnings and revenue analytics for guides:
 * - Monthly revenue metrics with MoM growth
 * - Year-to-date earnings
 * - Average booking value
 * - Platform fees breakdown
 * - Earnings summary with accrued/at-risk/safe buckets (SCRUM-541)
 * - Top performing experiences
 *
 * Epic: MVP 1.21.6 - Revenue Dashboard & Stripe-Dependent Features
 * Story: SCRUM-539 - Revenue Dashboard Backend (8 SP)
 * Story: SCRUM-541 - Guide Earnings Summary Dashboard (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/guides/revenue/summary
 * - Fetches from GET /api/v1/guides/revenue/by-experience
 * - Fetches from GET /api/v1/guides/revenue/earnings
 * - All data sourced from booking_ledger_entries (double-entry ledger)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  DollarSign,
  TrendingUp as _TrendingUp,
  TrendingDown,
  Calendar,
  Award,
  Info,
  Download,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KPICard from '../../components/common/KPICard';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Alert from '../../components/common/Alert';
import { formatCentsToDollars } from '../../utils/formHelpers';

export default function RevenueDashboard() {
  const navigate = useNavigate();
  const [revenueData, setRevenueData] = useState(null);
  const [topExperiences, setTopExperiences] = useState([]);
  const [earningsData, setEarningsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all revenue data in parallel
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [summaryRes, experiencesRes, earningsRes] = await Promise.all([
          apiRequest('/api/v1/guides/revenue/summary', { method: 'GET' }),
          apiRequest('/api/v1/guides/revenue/by-experience', { method: 'GET' }),
          apiRequest('/api/v1/guides/revenue/earnings', { method: 'GET' })
        ]);

        if (summaryRes.success) {
          setRevenueData(summaryRes.data);
        }
        if (experiencesRes.success) {
          setTopExperiences(experiencesRes.data.experiences || []);
        }
        if (earningsRes.success) {
          setEarningsData(earningsRes.data);
        }
      } catch (err) {
        if (err.status === 401) {
          setError('Session expired. Please log in again.');
        } else {
          setError(err.message || 'Failed to load revenue data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="guide" activeNavId="earnings" onNavigate={handleNavigate}>
        <div className="flex items-center justify-center min-h-screen">
          <LoadingSpinner message="Loading revenue data..." />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout userRole="guide" activeNavId="earnings" onNavigate={handleNavigate}>
        <Alert variant="error" message={error} />
      </DashboardLayout>
    );
  }

  const {
    revenue_this_month_cents = 0,
    revenue_last_month_cents = 0,
    revenue_ytd_cents = 0,
    avg_booking_value_cents = 0,
    projected_monthly_revenue_cents = 0,
    mom_growth_percent = 0,
    platform_fees_this_month_cents = 0
  } = revenueData || {};

  const isGrowthPositive = mom_growth_percent > 0;
  const netRevenueCents = revenue_this_month_cents - platform_fees_this_month_cents;

  return (
    <DashboardLayout
      userRole="guide"
      activeNavId="earnings"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Revenue & Earnings</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track your income, bookings, and financial performance
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => handleNavigate('transactions', '/guide/earnings/transactions')}
              className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Calendar className="w-5 h-5" />
              <span>View Transactions</span>
            </button>
            <button
              onClick={() => handleNavigate('export', '/guide/earnings/export')}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Download className="w-5 h-5" />
              <span>Export Ledger</span>
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* This Month */}
          <KPICard
            label="This Month"
            value={formatCentsToDollars(revenue_this_month_cents)}
            change={{
              value: `${isGrowthPositive ? '+' : ''}${mom_growth_percent.toFixed(1)}% from last month`,
              trend: isGrowthPositive ? 'up' : mom_growth_percent < 0 ? 'down' : 'neutral'
            }}
            icon={DollarSign}
            color="success"
          />

          {/* Last Month */}
          <KPICard
            label="Last Month"
            value={formatCentsToDollars(revenue_last_month_cents)}
            change={{
              value: 'Previous period',
              trend: 'neutral'
            }}
            icon={Calendar}
            color="primary"
          />

          {/* Year to Date */}
          <KPICard
            label="Year to Date"
            value={formatCentsToDollars(revenue_ytd_cents)}
            change={{
              value: `${new Date().getFullYear()}`,
              trend: 'neutral'
            }}
            icon={_TrendingUp}
            color="info"
          />

          {/* Avg Booking Value */}
          <KPICard
            label="Avg Booking"
            value={formatCentsToDollars(avg_booking_value_cents)}
            change={{
              value: 'Per booking',
              trend: 'neutral'
            }}
            icon={Award}
            color="warning"
          />
        </div>

        {/* Revenue Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Gross vs Net */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Gross Revenue</span>
                <span className="text-lg font-semibold text-gray-900">
                  {formatCentsToDollars(revenue_this_month_cents)}
                </span>
              </div>
              <div className="flex items-center justify-between text-red-600">
                <span className="text-sm flex items-center space-x-1">
                  <span>Platform Fees (8%)</span>
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    title="Platform fee includes 2.25% payment processing + 5.75% service fee"
                  >
                    <Info className="w-4 h-4" />
                  </button>
                </span>
                <span className="text-lg font-semibold">
                  -{formatCentsToDollars(platform_fees_this_month_cents)}
                </span>
              </div>
              <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-900">Net Earnings</span>
                <span className="text-xl font-bold text-green-600">
                  {formatCentsToDollars(netRevenueCents)}
                </span>
              </div>
            </div>
          </div>

          {/* Growth Indicator */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Month-over-Month</h3>
            <div className="flex flex-col items-center justify-center py-4">
              {isGrowthPositive ? (
                <_TrendingUp className="w-16 h-16 text-green-600 mb-3" />
              ) : mom_growth_percent < 0 ? (
                <TrendingDown className="w-16 h-16 text-red-600 mb-3" />
              ) : (
                <div className="w-16 h-16 flex items-center justify-center mb-3">
                  <div className="w-12 h-1 bg-gray-400 rounded"></div>
                </div>
              )}
              <p className={`text-4xl font-bold ${
                isGrowthPositive ? 'text-green-600' : mom_growth_percent < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
                {isGrowthPositive ? '+' : ''}{mom_growth_percent.toFixed(1)}%
              </p>
              <p className="text-sm text-gray-500 mt-2">vs. last month</p>
            </div>
          </div>

          {/* Projected */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Projected Monthly</h3>
            <div className="flex flex-col items-center justify-center py-4">
              <DollarSign className="w-16 h-16 text-blue-600 mb-3" />
              <p className="text-4xl font-bold text-gray-900">
                {formatCentsToDollars(projected_monthly_revenue_cents)}
              </p>
              <p className="text-sm text-gray-500 mt-2">Based on current pace</p>
            </div>
          </div>
        </div>

        {/* Earnings Summary - SCRUM-541 */}
        {earningsData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Earnings Summary</h2>
              <p className="text-xs text-gray-500 mt-1">
                All-time earnings breakdown from your booking ledger
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Safe Earnings */}
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="text-sm font-medium text-green-800">Safe Earnings</span>
                  </div>
                  <p className="text-2xl font-bold text-green-700">
                    {earningsData.safe?.formatted || '$0.00'}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Completed trips — fully earned
                  </p>
                </div>

                {/* At-Risk Earnings */}
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">At-Risk</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-700">
                    {earningsData.atRisk?.formatted || '$0.00'}
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    {earningsData.atRisk?.bookingCount || 0} upcoming booking{earningsData.atRisk?.bookingCount !== 1 ? 's' : ''} — may be cancelled
                  </p>
                </div>

                {/* Net Earnings */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center space-x-3 mb-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Net Earnings</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    {earningsData.netEarnings?.formatted || '$0.00'}
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Total accrued minus refunds
                  </p>
                </div>
              </div>

              {/* Detailed breakdown */}
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Total Accrued</span>
                  <p className="font-semibold text-gray-900">{earningsData.totalAccrued?.formatted || '$0.00'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Total Refunded</span>
                  <p className="font-semibold text-red-600">{earningsData.totalRefunded?.formatted || '$0.00'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Referral Commissions</span>
                  <p className="font-semibold text-purple-600">{earningsData.referralCommissions?.formatted || '$0.00'}</p>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated</span>
                  <p className="font-semibold text-gray-900">
                    {earningsData.lastUpdated ? new Date(earningsData.lastUpdated).toLocaleTimeString() : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Referral Earnings Summary */}
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Referral Earnings</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Earn commissions by referring guides to the platform
                </p>
              </div>
            </div>
            <button
              onClick={() => handleNavigate('referrals', '/guide/earnings/referrals')}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
            >
              <_TrendingUp className="w-5 h-5" />
              <span>View Referral Tracking</span>
            </button>
          </div>
        </div>

        {/* Top Performing Experiences */}
        {topExperiences && topExperiences.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Top Performing Experiences</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {topExperiences.map((experience, index) => {
                  return (
                    <div key={experience.experience_id} className="flex items-center space-x-4">
                      {/* Rank */}
                      <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        index === 0
                          ? 'bg-yellow-100 text-yellow-800'
                          : index === 1
                          ? 'bg-gray-200 text-gray-700'
                          : index === 2
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </div>

                      {/* Experience Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {experience.experience_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {experience.booking_count} booking{experience.booking_count !== 1 ? 's' : ''}
                        </p>
                      </div>

                      {/* Revenue */}
                      <div className="flex-shrink-0 text-right">
                        <p className="text-sm font-bold text-gray-900">
                          {formatCentsToDollars(experience.revenue_cents)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {experience.percentage.toFixed(1)}% of total
                        </p>
                      </div>

                      {/* Progress Bar */}
                      <div className="flex-shrink-0 w-24">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(experience.percentage, 100)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
